import React from 'react';
import { useMutation } from '@tanstack/react-query';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import {
  Box,
  IconButton,
  CircularProgress,
  Typography,
} from '@mui/material';
import {
  useDataProvider, useNotify, usePermissions, useRecordContext,
} from 'react-admin';
import ErrorIcon from '@mui/icons-material/Error';
import { IReport } from '../utilities/schemas/report';
import hasAccess from '../utilities/hasAccess';
import actions, { Actions } from '../constants/actions';
import { attributesSchema, checkIsTypeAllowed } from '../utilities/reports/permissions';

const DownloadFileButton = ({
  recordId,
  fileId,
  fileName,
  canDownload,
}: {
  recordId: string;
  fileId: string;
  fileName: string;
  canDownload: boolean;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const {
    mutate: handleDownloadClick,
    isPending,
  } = useMutation({
    mutationFn: async () => {
      try {
        const response = await dataProvider.getOne('reports/download', { id: `${recordId}/${fileId}` });
        window.open(response.data.downloadUrl, '_blank');
      } catch (e) {
        notify('Error: Could not download report', { type: 'warning' });
      }
    },
  });

  return (
    <Box display="flex" alignItems="center">
      <Typography margin={2}>
        {fileName}
      </Typography>
      {canDownload && (
        <IconButton
          onClick={() => handleDownloadClick()}
          color="secondary"
          disabled={isPending}
        >
          <CloudDownloadIcon />
        </IconButton>
      )}
    </Box>
  );
};

const DownloadReports = () => {
  const record = useRecordContext<IReport>();
  const { permissions } = usePermissions<Actions[]>();

  switch (record?.status) {
    case 'loading':
      return <CircularProgress size="1rem" />;
    case 'ready':
      return (
        <Box>
          {record?.files.map((file) => (
            <DownloadFileButton
              key={file.id}
              recordId={record?.id}
              fileId={file.id}
              fileName={file.fileName}
              canDownload={hasAccess(permissions, actions.REPORT_DOWNLOAD, {
                schema: attributesSchema,
                check: checkIsTypeAllowed(record?.type),
              })}
            />
          ))}
        </Box>
      );
    default:
      return <ErrorIcon />;
  }
};

export default DownloadReports;
